import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import storage from "../Assets/js/Global";
import ProtectedRoute from "./ProtectedRoute";
import { PublicRoute } from "./PublicRoute";
import Loader from "../Components/Loader";

// Entry screens stay eager so the very first paint has no lazy-load flash.
import Login from "../pages/auth/Login";
import LanguageSelector from "../pages/auth/LanguageSelector";

const Password = lazy(() => import("../pages/auth/Password"));
const ForgotPassword = lazy(() => import("../pages/auth/ForgotPassword"));
const VerifyOtp = lazy(() => import("../pages/auth/VerifyOtp"));
const ResetPassword = lazy(() => import("../pages/auth/ResetPassword"));

const Profile = lazy(() => import("../pages/profile/Profile"));
const UserProfileEdit = lazy(() => import("../pages/profile/UserProfileEdit"));
const HelpGuide = lazy(() => import("../pages/HelpGuide"));
const ChangePassword = lazy(() => import("../pages/ChangePassword"));
const DeleteAccount = lazy(() => import("../pages/DeleteAccount"));
const StartScan = lazy(() => import("../pages/scan/StartScan"));
const Dashboard = lazy(() => import("../pages/Dashboard"));
const ViewDetailsScreen = lazy(
  () => import("../pages/scanDetails/ViewDetailsScreen"),
);
const Documents = lazy(() => import("../pages/documents/Documents"));
const UploadDocument = lazy(() => import("../pages/documents/UploadDocument"));
const History = lazy(() => import("../pages/History"));
const PrivacyPolicy = lazy(() => import("../pages/PrivacyPolicy"));
const Articles = lazy(() => import("../pages/articles/Articles"));
const ArticlesDetails = lazy(() => import("../pages/articles/ArticlesDetails"));
const UserScanDetails = lazy(
  () => import("../pages/scanDetails/UserScanDetails"),
);
const TermsAndConditions = lazy(() => import("../pages/TermsAndConditions"));
const AboutUs = lazy(() => import("../pages/AboutUs"));
const WellnessInterests = lazy(
  () => import("../pages/auth/SignUp/WellnessInterests"),
);
const ExportScanApprove = lazy(() => import("../pages/ExportScanApprove"));
const ExportDocument = lazy(() => import("../pages/ExportDocument"));
const ViewExportList = lazy(() => import("../pages/ViewExportList"));
const Notification = lazy(() => import("../pages/Notification"));
const DeleteAccountV2 = lazy(() => import("../pages/DeleteAccount-v2"));
const GuestScan = lazy(() => import("../pages/scan/GuestScan"));
const GuestHistory = lazy(() => import("../pages/GuestHistory"));
const GuestList = lazy(() => import("../pages/GuestList"));
const SurveyForm = lazy(() => import("../pages/survey/SurveyForm"));
const ListCampaign = lazy(() => import("../pages/campaign/ListCampaign"));
const DetailsCampaign = lazy(() => import("../pages/campaign/DetailsCampaign"));
const CampaignScan = lazy(() => import("../pages/campaign/CampaignScan"));
const CampaignLanguageSelect = lazy(
  () => import("../pages/campaign/CampaignLanguageSelect"),
);
const CampaignFaceScan = lazy(
  () => import("../pages/campaign/CampaignFaceScan"),
);
const CampaignResult = lazy(() => import("../pages/campaign/CampaignResult"));
const CreateCampaign = lazy(() => import("../pages/campaign/CreateCampaign"));
const AccountDetails = lazy(
  () => import("../pages/auth/SignUp/AccountDetails"),
);
const PhysicalProfile = lazy(
  () => import("../pages/auth/SignUp/PhysicalProfile"),
);
const LifestyleQuestions = lazy(
  () => import("../pages/auth/SignUp/LifestyleQuestions"),
);
const WellnessPriorities = lazy(
  () => import("../pages/auth/SignUp/WellnessPriorities"),
);
const QrScanner = lazy(() => import("../pages/QrScanner"));
const ViewQuestions = lazy(() => import("../pages/profile/ViewQuestions"));
const RecommendedProduct = lazy(
  () => import("../pages/product/RecommendedProduct"),
);
const ProductDetails = lazy(() => import("../pages/product/ProductDetails"));
const AskAI = lazy(() => import("../pages/AskAI"));
const ChartsScreen = lazy(() => import("../pages/ChartsScreen"));
const FourLifeLogin = lazy(() => import("../pages/auth/FourLifeLogin"));
const Dashboard2 = lazy(() => import("../pages/Dashboard2"));
const MobileScanPrompt = lazy(() => import("../pages/scan/MobileScanPrompt"));
const ShowAllResult = lazy(() => import("../pages/ShowAllResult"));
const ShowAllResult2 = lazy(() => import("../pages/ShowAllResult2"));
const PlanExpired = lazy(() => import("../pages/PlanExpired"));
const FourLifeScanHistory = lazy(
  () => import("../pages/scanDetails/FourLifeScanHistory"),
);
const WebFaceScan = lazy(() => import("../pages/scan/WebFaceScan"));
const ScanResults = lazy(() => import("../pages/scan/ScanResults"));
const PublicScan = lazy(() => import("../pages/scan/PublicScan"));
const PublicWebFaceScan = lazy(() => import("../pages/scan/PublicWebFaceScan"));
const PublicScanResults = lazy(() => import("../pages/scan/PublicScanResults"));
const BPCalibration = lazy(() => import("../pages/BPCalibration"));

const NotFoundRedirect = () => {
  const loginObj = storage.get("loginObj");
  const isAuthenticated = loginObj && loginObj.userKey;
  const is4Life = process.env.REACT_APP_COMPANY_ID === "0it02f";

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={is4Life ? "/dashboard2" : "/dashboard"} replace />;
};

const AppRouter = () => {
  return (
    // Suspense shows the Loader while a lazily-loaded screen's chunk is fetched.
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route
          path="/"
          element={
            <PublicRoute>
              <LanguageSelector />
            </PublicRoute>
          }
        />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        {/* <Route
          path="/login-v2"
          element={
            <PublicRoute>
              <FourLifeLogin />
            </PublicRoute>
          }
        /> */}
        <Route
          path="/password"
          element={
            <PublicRoute>
              <Password />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/verify-otp"
          element={
            <PublicRoute>
              <VerifyOtp />
            </PublicRoute>
          }
        />
        <Route
          path="/signup/wellness-interests"
          element={
            <PublicRoute>
              <WellnessInterests />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute company="globalhealth">
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard2"
          element={
            <ProtectedRoute company="4life">
              <Dashboard2 />
            </ProtectedRoute>
          }
        />
        <Route
          path="/show-all-result"
          element={
            <ProtectedRoute>
              <ShowAllResult />
            </ProtectedRoute>
          }
        />
        <Route
          path="/show-all-result-2"
          element={
            <ProtectedRoute>
              <ShowAllResult2 />
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile-update"
          element={
            <ProtectedRoute>
              <UserProfileEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path="/language-selection"
          element={
            <ProtectedRoute>
              <LanguageSelector />
            </ProtectedRoute>
          }
        />
        <Route
          path="/deleteAcc-one"
          element={
            <ProtectedRoute>
              <DeleteAccount />
            </ProtectedRoute>
          }
        />
        <Route
          path="/delete-account"
          element={
            <PublicRoute>
              <DeleteAccountV2 />
            </PublicRoute>
          }
        />
        <Route
          path="/help-guide"
          element={
            <ProtectedRoute>
              <HelpGuide />
            </ProtectedRoute>
          }
        />
        <Route
          path="/change-password"
          element={
            <ProtectedRoute>
              <ChangePassword />
            </ProtectedRoute>
          }
        />

        <Route
          path="/start-scan"
          element={
            <ProtectedRoute>
              <StartScan />
            </ProtectedRoute>
          }
        />
        <Route
          path="/view-more-details"
          element={
            <ProtectedRoute>
              <ViewDetailsScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/documents"
          element={
            <ProtectedRoute>
              <Documents />
            </ProtectedRoute>
          }
        />

        <Route
          path="/upload-document"
          element={
            <ProtectedRoute>
              <UploadDocument />
            </ProtectedRoute>
          }
        />
        <Route
          path="/history"
          element={
            <ProtectedRoute>
              <History />
            </ProtectedRoute>
          }
        />
        <Route />
        <Route
          path="/privacy-policy"
          element={
            <ProtectedRoute>
              <PrivacyPolicy />
            </ProtectedRoute>
          }
        />
        <Route />
        <Route
          path="/terms-condition"
          element={
            <ProtectedRoute>
              <TermsAndConditions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/articles"
          element={
            <ProtectedRoute>
              <Articles />
            </ProtectedRoute>
          }
        />
        <Route
          path="/articles/:id"
          element={
            <ProtectedRoute>
              <ArticlesDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user-scan-details"
          element={
            <ProtectedRoute>
              <UserScanDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user-scan-details-four-life"
          element={
            <ProtectedRoute>
              <FourLifeScanHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/about-us"
          element={
            <ProtectedRoute>
              <AboutUs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/privacy-consent"
          element={
            <ProtectedRoute>
              <ExportScanApprove />
            </ProtectedRoute>
          }
        />

        <Route
          path="/export-document"
          element={
            <ProtectedRoute>
              <ExportDocument />
            </ProtectedRoute>
          }
        />
        <Route
          path="/view-export"
          element={
            <ProtectedRoute>
              <ViewExportList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notification"
          element={
            <ProtectedRoute>
              <Notification />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guest-scan"
          element={
            <ProtectedRoute>
              <GuestScan />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guest-history"
          element={
            <ProtectedRoute>
              <GuestHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guest-list"
          element={
            <ProtectedRoute>
              <GuestList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/survey-form"
          element={
            <ProtectedRoute>
              <SurveyForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invites-list"
          element={
            <ProtectedRoute>
              <ListCampaign />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invites-create"
          element={
            <ProtectedRoute>
              <CreateCampaign />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invites-details"
          element={
            <ProtectedRoute>
              <DetailsCampaign />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invite-language/:campaignCode"
          element={<CampaignLanguageSelect />}
        />
        <Route path="/invite/:campaignCode" element={<CampaignScan />} />
        <Route
          path="/invite/:campaignCode/face"
          element={<CampaignFaceScan />}
        />
        <Route
          path="/invite/:campaignCode/result"
          element={<CampaignResult />}
        />

        <Route
          path="/view-questions"
          element={
            <ProtectedRoute>
              <ViewQuestions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/recommended-product"
          element={
            <ProtectedRoute>
              <RecommendedProduct />
            </ProtectedRoute>
          }
        />
        <Route
          path="/product-details"
          element={
            <ProtectedRoute>
              <ProductDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/signup/account-details"
          element={
            <PublicRoute>
              <AccountDetails />
            </PublicRoute>
          }
        />
        <Route
          path="/signup/physical-profile"
          element={
            <PublicRoute>
              <PhysicalProfile />
            </PublicRoute>
          }
        />
        <Route
          path="/signup/lifestyle"
          element={
            <PublicRoute>
              <LifestyleQuestions />
            </PublicRoute>
          }
        />
        <Route
          path="/signup/wellness-priorities"
          element={
            <PublicRoute>
              <WellnessPriorities />
            </PublicRoute>
          }
        />
        <Route
          path="/qr-scanner"
          element={
            <PublicRoute>
              <QrScanner />
            </PublicRoute>
          }
        />
        <Route
          path="/web-scan"
          element={
            <ProtectedRoute>
              <MobileScanPrompt />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mobile-scan-prompt"
          element={
            <ProtectedRoute>
              <MobileScanPrompt />
            </ProtectedRoute>
          }
        />
        <Route
          path="/scan-qr-code"
          element={
            <ProtectedRoute>
              <MobileScanPrompt />
            </ProtectedRoute>
          }
        />
        <Route
          path="/web-face-scan"
          element={
            <ProtectedRoute>
              <WebFaceScan />
            </ProtectedRoute>
          }
        />
        <Route
          path="/scan-results"
          element={
            <ProtectedRoute>
              <ScanResults />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ask-ai"
          element={
            <ProtectedRoute>
              <AskAI />
            </ProtectedRoute>
          }
        />
        <Route
          path="/charts-screen"
          element={
            <ProtectedRoute>
              <ChartsScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/plan-expired"
          element={
            <ProtectedRoute>
              <PlanExpired />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bp-calibration"
          element={
            <ProtectedRoute>
              <BPCalibration />
            </ProtectedRoute>
          }
        />
        {/* Public standalone scan flow (no login required) */}
        <Route path="/scan/:scanCode" element={<PublicScan />} />
        <Route path="/scan/:scanCode/face" element={<PublicWebFaceScan />} />
        <Route path="/scan/:scanCode/result" element={<PublicScanResults />} />
        {/* Catch-all: unknown routes redirect by auth + company */}
        <Route path="*" element={<NotFoundRedirect />} />
      </Routes>
    </Suspense>
  );
};

export default AppRouter;
